import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { IMockup, IMockupState } from "Interfaces/interface.mockupGenerator";
import toast from "react-hot-toast";
import mockupService from "services/mockupService";
import { extractErrorMessage } from "utils/ErrorHandlers";

const initialState: IMockupState = {
  mockups: [],
  loading: false,
  error: null,
  isMockupLoaded: false,
  mockupFetchError: null,
};

export const fetchUserMockups = createAsyncThunk<
  IMockup[],
  void,
  { rejectValue: string }
>("mockup/fetchUserMockups", async (_, { rejectWithValue }) => {
  try {
    const response = await mockupService.userMockup();
    return response;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message || "Failed to fetch mockups");
    return rejectWithValue(message || "Failed to fetch mockups");
  }
});

export const deleteUserMockup = createAsyncThunk<
  IMockup[],
  string,
  { rejectValue: string }
>("catalog/deleteUserCatalog", async (mockupId, { rejectWithValue }) => {
  try {
    const response = await mockupService.deleteMockup(mockupId);
    toast.success("Mockup deleted successfully");
    return response.updatedMockups;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message || "Failed to delete mockups");
    return rejectWithValue(message || "Failed to delete mockups");
  }
});

const mockupSlice = createSlice({
  name: "mockup",
  initialState,
  reducers: {
    clearMockupState: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserMockups.pending, (state) => {
        state.loading = true;
        state.mockupFetchError = null;
      })
      .addCase(fetchUserMockups.fulfilled, (state, action) => {
        state.mockups = action.payload;
        state.loading = false;
        state.isMockupLoaded = true;
      })
      .addCase(fetchUserMockups.rejected, (state, action) => {
        state.loading = false;
        state.mockupFetchError = action.payload as string;
      })

      .addCase(deleteUserMockup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUserMockup.fulfilled, (state, action) => {
        state.mockups = action.payload;
        state.loading = false;
        state.isMockupLoaded = false;
      })
      .addCase(deleteUserMockup.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      });
  },
});

export const { clearMockupState } = mockupSlice.actions;
export default mockupSlice.reducer;

export const selectUserMockups = (state: any) => state.mockup.mockups || [];
export const selectMockupFetchError = (state: any) =>
  state.mockup.mockupFetchError;
export const selectUserMockupsLoading = (state: any) => state.mockup.loading;
export const selectUserMockupsLoaded = (state: any) =>
  state.mockup.isMockupLoaded;
export const selectUserMockupsError = (state: any) => state.mockup.error;
